import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  importProductionPlan,
  validateProductionPlanImport,
} from "../api/workCenterQueue";

const DATE_HEADER_KEYS = ["tarih", "gun", "gün", "date"];

function normHeader(s) {
  return String(s ?? "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, "");
}

function isDateHeader(s) {
  const n = normHeader(s);
  return DATE_HEADER_KEYS.some((k) => n === k || n.startsWith(k));
}

function isOlcu(s) {
  return normHeader(s).startsWith("olcu") || normHeader(s).startsWith("ölcu");
}
function isKalip(s) {
  return normHeader(s).startsWith("kalip") || normHeader(s).startsWith("kalıp");
}
function toDateOnly(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const dm = s.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (dm) return `${dm[3]}-${dm[2]}-${dm[1]}`;
  return null;
}

function toStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

/**
 * Excel'i iş merkezi kolonlarına göre çözümle.
 * İlk satır başlık. Her satır bir tarih. Ana iş merkezi kolonunu takip eden
 * OLCU/KALIP kolonları o iş merkezine bağlanır.
 */
function parseSheet(sheet) {
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: true,
    cellDates: true,
  });
  if (!rows || rows.length < 2) {
    return { dateColIndex: -1, workCenters: [], dataRows: [], rawHeaders: [] };
  }

  const headerRow = rows[0] || [];
  let dateColIndex = -1;
  const workCenters = []; // [{code, colIndex, olcuCol, kalipCol}]

  for (let i = 0; i < headerRow.length; i++) {
    const cell = headerRow[i];
    const text = toStr(cell);
    if (!text) continue;

    if (dateColIndex < 0 && isDateHeader(text)) {
      dateColIndex = i;
      continue;
    }

    if (workCenters.length > 0) {
      const last = workCenters[workCenters.length - 1];
      if (isOlcu(text)) { last.olcuCol = i; continue; }
      if (isKalip(text)) { last.kalipCol = i; continue; }
    }

    // Ana iş merkezi kolonu (BSK-01, KSM-01, ...). Kodu büyük harfe çevir.
    workCenters.push({
      code: text.toUpperCase(),
      colIndex: i,
      olcuCol: -1,
      kalipCol: -1,
    });
  }

  // Tarih kolonu tespit edilemediyse ilk kolonu tarih kabul et.
  if (dateColIndex < 0) dateColIndex = 0;

  const dataRows = rows.slice(1).filter((r) => {
    if (!Array.isArray(r)) return false;
    return r.some((c) => toStr(c) !== "");
  });

  return { dateColIndex, workCenters, dataRows, rawHeaders: headerRow };
}

function buildImportRows({ dateColIndex, workCenters, dataRows }) {
  const out = [];
  for (let ri = 0; ri < dataRows.length; ri++) {
    const row = dataRows[ri];
    const scheduled_date = toDateOnly(row[dateColIndex]);

    for (const wc of workCenters) {
      const cell = toStr(row[wc.colIndex]);
      if (!cell) continue;

      const olcu = wc.olcuCol >= 0 ? row[wc.olcuCol] : "";
      const kalip = wc.kalipCol >= 0 ? row[wc.kalipCol] : "";

      out.push({
        _excel_row: ri + 2, // 1 header + 0-idx
        work_center_code: wc.code,
        scheduled_date,
        order_no: cell,
        diameter_cm: olcu !== "" ? Number(String(olcu).replace(",", ".")) : null,
        mold_type: toStr(kalip) || null,
      });
    }
  }
  return out;
}

export default function ProductionPlanImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);
  const [validated, setValidated] = useState(null); // backend response
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  function handleFile(e) {
    const file = e.target?.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError("");
    setValidated(null);
    setResult(null);
    setSubmitError("");

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        if (!sheet) throw new Error("Çalışma sayfası bulunamadı.");
        const parsed = parseSheet(sheet);
        if (!parsed.workCenters.length) {
          throw new Error("Başlık satırında iş merkezi kolonu bulunamadı.");
        }
        const importRows = buildImportRows(parsed);
        if (!importRows.length) {
          throw new Error("İşlenecek satır bulunamadı (tüm hücreler boş).");
        }
        setRows(importRows);
        setValidating(true);
        try {
          const res = await validateProductionPlanImport(importRows);
          setValidated(res);
        } catch (err) {
          setParseError(
            err?.response?.data?.error?.message || "Doğrulama başarısız."
          );
        } finally {
          setValidating(false);
        }
      } catch (err) {
        setParseError(err?.message || "Excel okunamadı.");
        setRows([]);
        setValidated(null);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const { errorCount, rowsByIndex } = useMemo(() => {
    const map = new Map();
    const errors = validated?.errors || [];
    for (const e of errors) map.set(e.index, e.errors);
    return { errorCount: errors.length, rowsByIndex: map };
  }, [validated]);

  const canSubmit =
    !!rows.length && !validating && !saving && errorCount === 0 && !!validated;

  async function handleSubmit() {
    setSaving(true);
    setSubmitError("");
    setResult(null);
    try {
      const res = await importProductionPlan(rows);
      setResult(res);
      if (typeof onImported === "function") onImported(res);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.error?.message || "İçe aktarma başarısız."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Planlama Excel İçe Aktar</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Satırlar tarih, kolonlar iş merkezi kodu (BSK-01, KSM-01, KSM-02, AYK-01,
            FRM-01…FRM-15, SHRK-01, SHRK-02, KOLI-01). Hücre değeri iş emri no.
            OLCU/KALIP kolonları bilgi olarak okunur; bıçak seçimi iş emri sayfasından yapılır.
          </Alert>

          <Box>
            <Button variant="outlined" component="label">
              Excel Dosyası Seç
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFile}
              />
            </Button>
            {fileName ? (
              <Typography variant="caption" sx={{ ml: 2 }}>
                {fileName}
              </Typography>
            ) : null}
          </Box>

          {parseError ? <Alert severity="error">{parseError}</Alert> : null}

          {validating ? <Typography variant="body2">Doğrulanıyor…</Typography> : null}

          {validated && errorCount > 0 ? (
            <Alert severity="warning">
              {errorCount} satırda hata var. Tümü giderilmeden oluşturulamaz.
            </Alert>
          ) : null}

          {validated && errorCount === 0 && rows.length > 0 ? (
            <Alert severity="success">
              {rows.length} satır doğrulandı. Oluşturmaya hazır.
            </Alert>
          ) : null}

          {rows.length > 0 ? (
            <Box sx={{ maxHeight: 420, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Tarih</TableCell>
                    <TableCell>İş Merkezi</TableCell>
                    <TableCell>İş Emri</TableCell>
                    <TableCell>Ölçü</TableCell>
                    <TableCell>Kalıp</TableCell>
                    <TableCell>Hata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const errs = rowsByIndex.get(i) || [];
                    return (
                      <TableRow
                        key={i}
                        sx={errs.length ? { bgcolor: "#fef2f2" } : {}}
                      >
                        <TableCell>{r._excel_row}</TableCell>
                        <TableCell>{r.scheduled_date || "—"}</TableCell>
                        <TableCell>{r.work_center_code}</TableCell>
                        <TableCell>{r.order_no}</TableCell>
                        <TableCell>{r.diameter_cm ?? "—"}</TableCell>
                        <TableCell>{r.mold_type ?? "—"}</TableCell>
                        <TableCell>
                          {errs.length ? (
                            <Stack spacing={0.3}>
                              {errs.map((m, j) => (
                                <Chip
                                  key={j}
                                  label={m}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Chip label="OK" size="small" color="success" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ) : null}

          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity="success">
              {result.created} satır {result.groups?.length || 0} iş merkezine kaydedildi.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {saving ? "Oluşturuluyor…" : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
